import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import {
  ArrowLeft, ArrowRight, Check, Loader2, Save,
  ClipboardList, MapPin, Settings, Users, Database, CheckSquare, Rocket, FileCheck,
} from 'lucide-react';
import api, { fetchAllPages } from '../../lib/api';
import SearchSelect from '@/components/common/SearchSelect';
import { roleLabel } from '@/lib/roleLabels';

const STEPS = [
  { key: 'step1', label: 'Basics', icon: ClipboardList },
  { key: 'step2', label: 'Scope', icon: MapPin },
  { key: 'step3', label: 'Modules', icon: Settings },
  { key: 'step4', label: 'Ownership', icon: Users },
  { key: 'step5', label: 'Migration', icon: Database },
  { key: 'step6', label: 'Checklist', icon: CheckSquare },
  { key: 'step7', label: 'Pilot Config', icon: Rocket },
  { key: 'step8', label: 'Confirm', icon: FileCheck },
];

const MODULES = ['participants', 'workforce', 'appointments', 'service_catalog', 'compliance', 'invoicing', 'care_plans', 'reporting'];
const TYPES = [
  { value: 'pilot', label: 'Pilot Rollout' },
  { value: 'go_live', label: 'Go-Live Setup' },
  { value: 'data_migration', label: 'Data Migration' },
  { value: 'implementation', label: 'Implementation' },
];
const PILOT_STAGES = [
  { value: 'stage_1', label: 'Stage 1 - Core Operations' },
  { value: 'stage_2', label: 'Stage 2 - Controlled Financials' },
  { value: 'stage_3', label: 'Stage 3 - Full Platform' },
];

export default function OnboardingWizardPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [onboardingId, setOnboardingId] = useState(null);
  const [users, setUsers] = useState([]);
  const [states, setStates] = useState([]);
  const [branches, setBranches] = useState([]);
  const [formData, setFormData] = useState({
    onboardingName: '', type: 'pilot', description: '',
    targetStartDate: '', targetGoLiveDate: '', rolloutNotes: '',
    scope: { states: [], branches: [], services: '', modules: [], notes: '' },
    moduleConfig: { enableParticipantImport: false, enableStaffImport: false, enableServiceCatalogImport: false, enableFundingImport: false, enableDocumentMigration: false, modules: [] },
    ownership: { implementationOwnerUserId: '', reviewerUserId: '', activationApproverUserId: '', supportContacts: '', accessNotes: '' },
    migrationConfig: { sourceSystem: '', datasets: '', mappingNotes: '', dataCleansingNotes: '', importDependencies: '' },
    checklist: { configurationComplete: false, datasetsUploaded: false, validationPassed: false, branchStateMappingComplete: false, servicesConfigured: false, accessReviewed: false, pilotControlsConfigured: false, complianceReviewComplete: false },
    pilotConfig: { pilotEnabled: false, targetPilotStage: '', featureFlagsRequested: '', allowedStates: [], allowedBranches: [], operationalSafeguards: '', rollbackNotes: '' },
    confirmation: { adminConfirm: false, reviewerConfirm: false, goLiveNotes: '' },
  });

  // Load reference data
  useEffect(() => {
    const load = async () => {
      try {
        const [usersRes, statesRes, branchesRes] = await Promise.all([
          fetchAllPages('/admin/users').catch(() => []),   // NEW-007: `limit` was ignored → only the first 20 users
          api.get('/org/states').catch(() => ({ data: { data: [] } })),
          api.get('/org/branches').catch(() => ({ data: { data: [] } })),
        ]);
        setUsers((usersRes || []).filter(u => u.is_active !== false && u.status !== 'inactive'));
        setStates(statesRes.data?.data || []);
        setBranches(branchesRes.data?.data || []);
      } catch {}
    };
    load();
  }, []);

  const updateField = (path, value) => {
    setFormData(prev => {
      const keys = path.split('.');
      const copy = JSON.parse(JSON.stringify(prev));
      let obj = copy;
      for (let i = 0; i < keys.length - 1; i++) obj = obj[keys[i]];
      obj[keys[keys.length - 1]] = value;
      return copy;
    });
  };

  const handleCreate = async () => {
    if (!formData.onboardingName.trim()) { toast.error('Onboarding name is required'); return false; }
    if (formData.targetStartDate && formData.targetGoLiveDate && formData.targetGoLiveDate < formData.targetStartDate) {
      toast.error('Go-live date cannot be before start date'); return false;
    }
    setSaving(true);
    try {
      const res = await api.post('/client-onboarding', {
        onboardingName: formData.onboardingName,
        type: formData.type,
        description: formData.description,
        targetStartDate: formData.targetStartDate || null,
        targetGoLiveDate: formData.targetGoLiveDate || null,
      });
      setOnboardingId(res.data.id);
      toast.success(`Project created: ${res.data.onboardingCode}`);
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data;
      const msg = detail?.error?.message || (Array.isArray(detail) ? detail[0]?.msg : 'Failed to create');
      toast.error(msg);
      return false;
    } finally { setSaving(false); }
  };

  const handleSaveStep = async (stepKey) => {
    if (!onboardingId) return false;
    setSaving(true);
    try {
      const stepData = buildStepData(stepKey);
      await api.put(`/client-onboarding/${onboardingId}/steps/${stepKey}`, stepData);
      toast.success(`${STEPS.find(s => s.key === stepKey)?.label || stepKey} saved`);
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail || e.response?.data;
      toast.error(detail?.error?.message || 'Failed to save step');
      return false;
    } finally { setSaving(false); }
  };

  const buildStepData = (stepKey) => {
    switch (stepKey) {
      case 'step1': return { onboardingName: formData.onboardingName, type: formData.type, description: formData.description, dates: { targetStartDate: formData.targetStartDate, targetGoLiveDate: formData.targetGoLiveDate } };
      case 'step2': return { scope: { states: formData.scope.states, branches: formData.scope.branches, services: typeof formData.scope.services === 'string' ? formData.scope.services.split(',').map(s => s.trim()).filter(Boolean) : formData.scope.services, modules: formData.moduleConfig.modules, notes: formData.scope.notes } };
      case 'step3': return { moduleConfig: { ...formData.moduleConfig } };
      case 'step4': return { ownership: { implementationOwnerUserId: formData.ownership.implementationOwnerUserId || null, reviewerUserId: formData.ownership.reviewerUserId || null, activationApproverUserId: formData.ownership.activationApproverUserId || null, supportContacts: formData.ownership.supportContacts ? [{ name: formData.ownership.supportContacts }] : [] } };
      case 'step5': return { migrationConfig: { sourceSystem: formData.migrationConfig.sourceSystem, datasets: formData.migrationConfig.datasets.split(',').map(s => s.trim()).filter(Boolean), mappingNotes: formData.migrationConfig.mappingNotes, dataCleansingNotes: formData.migrationConfig.dataCleansingNotes, importDependencies: formData.migrationConfig.importDependencies.split(',').map(s => s.trim()).filter(Boolean) } };
      case 'step6': return { checklist: { ...formData.checklist } };
      case 'step7': return { pilotConfig: { pilotEnabled: formData.pilotConfig.pilotEnabled, targetPilotStage: formData.pilotConfig.targetPilotStage || null, featureFlagsRequested: formData.pilotConfig.featureFlagsRequested.split(',').map(s => s.trim()).filter(Boolean), allowedStates: formData.pilotConfig.allowedStates, allowedBranches: formData.pilotConfig.allowedBranches, operationalSafeguards: typeof formData.pilotConfig.operationalSafeguards === 'string' ? formData.pilotConfig.operationalSafeguards.split(',').map(s => s.trim()).filter(Boolean) : formData.pilotConfig.operationalSafeguards, rollbackNotes: formData.pilotConfig.rollbackNotes } };
      case 'step8': return { confirmation: formData.confirmation };
      default: return {};
    }
  };

  const handleNext = async () => {
    const step = STEPS[currentStep];
    if (currentStep === 0 && !onboardingId) {
      const ok = await handleCreate();
      if (!ok) return;
      const saved = await handleSaveStep('step1');
      if (saved) setCurrentStep(c => c + 1);
    } else {
      const saved = await handleSaveStep(step.key);
      if (saved) setCurrentStep(c => Math.min(c + 1, STEPS.length - 1));
    }
  };

  const handleBack = () => setCurrentStep(c => Math.max(c - 1, 0));

  const handleFinish = async () => {
    if (!formData.confirmation.adminConfirm) { toast.error('Admin confirmation required'); return; }
    await handleSaveStep('step8');
    toast.success('Onboarding project saved successfully!');
    navigate(`/admin/onboarding/${onboardingId}`);
  };

  const step = STEPS[currentStep];

  return (
    <div className="space-y-6 max-w-4xl" data-testid="onboarding-wizard">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/onboarding')} data-testid="back-to-dashboard">
          <ArrowLeft className="h-4 w-4 mr-1" />Back
        </Button>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">New Onboarding Project</h1>
          <p className="text-sm text-muted-foreground">Step {currentStep + 1} of {STEPS.length}: {step.label}</p>
        </div>
      </div>

      {/* Step Navigator */}
      <div className="flex gap-1 overflow-x-auto pb-2" data-testid="step-navigator">
        {STEPS.map((s, i) => {
          const Icon = s.icon;
          const isActive = i === currentStep;
          const isDone = onboardingId && i < currentStep;
          return (
            <button key={s.key} disabled={!onboardingId && i > 0}
              onClick={() => onboardingId && setCurrentStep(i)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${isActive ? 'bg-primary text-primary-foreground' : isDone ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-muted text-muted-foreground'}`}
              data-testid={`step-${s.key}`}>
              {isDone ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
              {s.label}
            </button>
          );
        })}
      </div>

      <Card>
        <CardContent className="pt-6">
          {currentStep === 0 && <StepBasics formData={formData} updateField={updateField} />}
          {currentStep === 1 && <StepScope formData={formData} updateField={updateField} states={states} branches={branches} />}
          {currentStep === 2 && <StepModules formData={formData} updateField={updateField} />}
          {currentStep === 3 && <StepOwnership formData={formData} updateField={updateField} users={users} />}
          {currentStep === 4 && <StepMigration formData={formData} updateField={updateField} />}
          {currentStep === 5 && <StepChecklist formData={formData} updateField={updateField} />}
          {currentStep === 6 && <StepPilotConfig formData={formData} updateField={updateField} states={states} branches={branches} />}
          {currentStep === 7 && <StepConfirmation formData={formData} updateField={updateField} />}
        </CardContent>
      </Card>

      <div className="flex justify-between">
        <Button variant="outline" onClick={handleBack} disabled={currentStep === 0 || saving} data-testid="wizard-back">
          <ArrowLeft className="h-4 w-4 mr-1" />Back
        </Button>
        <div className="flex gap-2">
          {currentStep < STEPS.length - 1 ? (
            <Button onClick={handleNext} disabled={saving} data-testid="wizard-next">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {currentStep === 0 && !onboardingId ? 'Create & Continue' : 'Save & Continue'}
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button onClick={handleFinish} disabled={saving} data-testid="wizard-finish">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
              Complete Setup
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function StepBasics({ formData, updateField }) {
  return (
    <div className="space-y-4" data-testid="step-basics">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <Label>Onboarding Name *</Label>
          <Input value={formData.onboardingName} onChange={e => updateField('onboardingName', e.target.value)}
                 placeholder="e.g. Pilot Rollout - April 2026" data-testid="input-name" />
        </div>
        <div>
          <Label>Type *</Label>
          <Select value={formData.type} onValueChange={v => updateField('type', v)}>
            <SelectTrigger data-testid="input-type"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Target Start Date</Label>
          <Input type="date" value={formData.targetStartDate} onChange={e => updateField('targetStartDate', e.target.value)} data-testid="input-start-date" />
        </div>
        <div>
          <Label>Target Go-Live Date</Label>
          <Input type="date" value={formData.targetGoLiveDate} onChange={e => updateField('targetGoLiveDate', e.target.value)} data-testid="input-golive-date" />
          {formData.targetStartDate && formData.targetGoLiveDate && formData.targetGoLiveDate < formData.targetStartDate && (
            <p className="text-xs text-destructive mt-1">Go-live date cannot be before start date</p>
          )}
        </div>
      </div>
      <div>
        <Label>Description</Label>
        <Textarea value={formData.description} onChange={e => updateField('description', e.target.value)}
                  placeholder="Describe this onboarding project..." rows={3} data-testid="input-description" />
      </div>
    </div>
  );
}

function StepScope({ formData, updateField, states, branches }) {
  const toggleArrayItem = (path, item) => {
    const current = path.split('.').reduce((o, k) => o?.[k], formData) || [];
    const updated = current.includes(item) ? current.filter(i => i !== item) : [...current, item];
    updateField(path, updated);
  };
  return (
    <div className="space-y-4" data-testid="step-scope">
      <p className="text-sm text-muted-foreground">Configure the internal scope for this onboarding project.</p>
      <div>
        <Label>State Scope</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {states.length > 0 ? states.map(s => (
            <label key={s.code} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition-colors ${(formData.scope.states || []).includes(s.code) ? 'bg-primary/10 border-primary text-primary' : 'hover:bg-muted'}`}>
              <Checkbox checked={(formData.scope.states || []).includes(s.code)} onCheckedChange={() => toggleArrayItem('scope.states', s.code)} data-testid={`state-${s.code}`} />
              {s.name || s.code}
            </label>
          )) : <p className="text-xs text-muted-foreground">No states configured</p>}
        </div>
      </div>
      <div>
        <Label>Branch Scope</Label>
        <div className="flex flex-wrap gap-2 mt-1">
          {branches.length > 0 ? branches.map(b => (
            <label key={b.code} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm cursor-pointer transition-colors ${(formData.scope.branches || []).includes(b.code) ? 'bg-primary/10 border-primary text-primary' : 'hover:bg-muted'}`}>
              <Checkbox checked={(formData.scope.branches || []).includes(b.code)} onCheckedChange={() => toggleArrayItem('scope.branches', b.code)} data-testid={`branch-${b.code}`} />
              {b.name || b.code}
            </label>
          )) : <p className="text-xs text-muted-foreground">No branches configured</p>}
        </div>
      </div>
      <div>
        <Label>Service Scope (comma-separated)</Label>
        <Input value={formData.scope.services} onChange={e => updateField('scope.services', e.target.value)} placeholder="Community, SIL" data-testid="input-services" />
      </div>
      <div>
        <Label>Scope Notes</Label>
        <Textarea value={formData.scope.notes} onChange={e => updateField('scope.notes', e.target.value)}
                  placeholder="Operational scope notes..." rows={2} data-testid="input-scope-notes" />
      </div>
    </div>
  );
}

function StepModules({ formData, updateField }) {
  const toggleModule = (mod) => {
    const current = formData.moduleConfig.modules || [];
    const updated = current.includes(mod) ? current.filter(m => m !== mod) : [...current, mod];
    updateField('moduleConfig.modules', updated);
  };
  return (
    <div className="space-y-4" data-testid="step-modules">
      <p className="text-sm text-muted-foreground">Select modules involved and import configuration.</p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {MODULES.map(mod => (
          <label key={mod} className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-colors ${(formData.moduleConfig.modules || []).includes(mod) ? 'bg-primary/5 border-primary' : 'hover:bg-muted'}`}>
            <Checkbox checked={(formData.moduleConfig.modules || []).includes(mod)} onCheckedChange={() => toggleModule(mod)} data-testid={`module-${mod}`} />
            <span className="text-sm capitalize">{mod.replace('_', ' ')}</span>
          </label>
        ))}
      </div>
      <Separator />
      <p className="text-sm font-medium">Import Configuration</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {[
          { key: 'enableParticipantImport', label: 'Enable Participant Import' },
          { key: 'enableStaffImport', label: 'Enable Staff Import' },
          { key: 'enableServiceCatalogImport', label: 'Enable Service Catalog Import' },
          { key: 'enableFundingImport', label: 'Enable Funding Import' },
          { key: 'enableDocumentMigration', label: 'Enable Document Migration' },
        ].map(item => (
          <label key={item.key} className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-muted">
            <Checkbox checked={formData.moduleConfig[item.key]} onCheckedChange={v => updateField(`moduleConfig.${item.key}`, v)} data-testid={`config-${item.key}`} />
            <span className="text-sm">{item.label}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function UserSelector({ label, value, onChange, users, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <SearchSelect
        options={users.map(u => ({
          value: u.id,
          label: `${u.full_name || `${u.first_name || ''} ${u.last_name || ''}`.trim() || u.email} (${roleLabel(u.role_name) || 'Unknown'})`,
          keywords: u.email || '',
        }))}
        value={value}
        onChange={v => onChange(v || '')}
        placeholder="Select user..."
        searchPlaceholder="Search users by name or email…"
        emptyText="No users match that search"
        clearLabel="-- Not assigned --"
        testId={testId}
      />
    </div>
  );
}

function StepOwnership({ formData, updateField, users }) {
  const adminUsers = users.filter(u => ['Super Admin', 'Admin', 'Org Admin', 'Coordinator'].includes(u.role_name));
  return (
    <div className="space-y-4" data-testid="step-ownership">
      <p className="text-sm text-muted-foreground">Assign ownership and access for this project.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <UserSelector label="Implementation Owner *" value={formData.ownership.implementationOwnerUserId}
          onChange={v => updateField('ownership.implementationOwnerUserId', v)} users={adminUsers} testId="input-owner" />
        <UserSelector label="Reviewer" value={formData.ownership.reviewerUserId}
          onChange={v => updateField('ownership.reviewerUserId', v)} users={adminUsers} testId="input-reviewer" />
        <UserSelector label="Activation Approver *" value={formData.ownership.activationApproverUserId}
          onChange={v => updateField('ownership.activationApproverUserId', v)}
          users={users.filter(u => ['Super Admin', 'Admin', 'Org Admin'].includes(u.role_name))} testId="input-approver" />
        <div>
          <Label>Support Contacts</Label>
          <Input value={formData.ownership.supportContacts} onChange={e => updateField('ownership.supportContacts', e.target.value)} placeholder="Support contact name" data-testid="input-support" />
        </div>
      </div>
      <div>
        <Label>Access Notes</Label>
        <Textarea value={formData.ownership.accessNotes} onChange={e => updateField('ownership.accessNotes', e.target.value)}
                  placeholder="Access and permission notes..." rows={2} data-testid="input-access-notes" />
      </div>
    </div>
  );
}

function StepMigration({ formData, updateField }) {
  return (
    <div className="space-y-4" data-testid="step-migration">
      <p className="text-sm text-muted-foreground">Configure data migration and import setup.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <Label>Source System</Label>
          <Input value={formData.migrationConfig.sourceSystem} onChange={e => updateField('migrationConfig.sourceSystem', e.target.value)} placeholder="e.g. ShiftCare, Brevity" data-testid="input-source-system" />
        </div>
        <div>
          <Label>Datasets (comma-separated)</Label>
          <Input value={formData.migrationConfig.datasets} onChange={e => updateField('migrationConfig.datasets', e.target.value)} placeholder="participants, staff, services" data-testid="input-datasets" />
        </div>
      </div>
      <div>
        <Label>Mapping Notes</Label>
        <Textarea value={formData.migrationConfig.mappingNotes} onChange={e => updateField('migrationConfig.mappingNotes', e.target.value)} placeholder="Field mapping notes..." rows={2} data-testid="input-mapping-notes" />
      </div>
      <div>
        <Label>Data Cleansing Notes</Label>
        <Textarea value={formData.migrationConfig.dataCleansingNotes} onChange={e => updateField('migrationConfig.dataCleansingNotes', e.target.value)} placeholder="Data quality notes..." rows={2} data-testid="input-cleansing-notes" />
      </div>
      <div>
        <Label>Import Dependencies (comma-separated)</Label>
        <Input value={formData.migrationConfig.importDependencies} onChange={e => updateField('migrationConfig.importDependencies', e.target.value)} placeholder="e.g. branches setup, services configured" data-testid="input-import-deps" />
      </div>
    </div>
  );
}

function StepChecklist({ formData, updateField }) {
  const items = [
    { key: 'configurationComplete', label: 'Configuration Complete' },
    { key: 'datasetsUploaded', label: 'Required Datasets Uploaded' },
    { key: 'validationPassed', label: 'Validation Passed' },
    { key: 'branchStateMappingComplete', label: 'Branch/State Mapping Complete' },
    { key: 'servicesConfigured', label: 'Services Configured' },
    { key: 'accessReviewed', label: 'Access Reviewed' },
    { key: 'pilotControlsConfigured', label: 'Pilot Controls Configured' },
    { key: 'complianceReviewComplete', label: 'Compliance Review Complete' },
  ];
  const done = items.filter(i => formData.checklist[i.key]).length;
  return (
    <div className="space-y-4" data-testid="step-checklist">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Review and confirm readiness checklist items.</p>
        <Badge variant="outline">{done}/{items.length} complete</Badge>
      </div>
      <div className="space-y-2">
        {items.map(item => (
          <label key={item.key} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${formData.checklist[item.key] ? 'bg-emerald-50 border-emerald-200' : 'hover:bg-muted'}`}>
            <Checkbox checked={formData.checklist[item.key]} onCheckedChange={v => updateField(`checklist.${item.key}`, v)} data-testid={`checklist-${item.key}`} />
            <span className="text-sm">{item.label}</span>
            {formData.checklist[item.key] && <Check className="h-4 w-4 text-emerald-600 ml-auto" />}
          </label>
        ))}
      </div>
    </div>
  );
}

function StepPilotConfig({ formData, updateField, states, branches }) {
  const toggleArrayItem = (path, item) => {
    const current = path.split('.').reduce((o, k) => o?.[k], formData) || [];
    const updated = current.includes(item) ? current.filter(i => i !== item) : [...current, item];
    updateField(path, updated);
  };
  return (
    <div className="space-y-4" data-testid="step-pilot-config">
      <p className="text-sm text-muted-foreground">Configure pilot activation settings. Links to Day 24 rollout controls.</p>
      <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted">
        <Checkbox checked={formData.pilotConfig.pilotEnabled} onCheckedChange={v => updateField('pilotConfig.pilotEnabled', v)} data-testid="pilot-enabled" />
        <div>
          <span className="text-sm font-medium">Enable Pilot Mode</span>
          <p className="text-xs text-muted-foreground">Activate pilot rollout when onboarding is complete</p>
        </div>
      </label>
      {formData.pilotConfig.pilotEnabled && (
        <div className="space-y-4 pl-4 border-l-2 border-primary/20">
          <div>
            <Label>Target Pilot Stage</Label>
            <Select value={formData.pilotConfig.targetPilotStage} onValueChange={v => updateField('pilotConfig.targetPilotStage', v)}>
              <SelectTrigger data-testid="pilot-stage-select"><SelectValue placeholder="Select stage" /></SelectTrigger>
              <SelectContent>
                {PILOT_STAGES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Feature Flags to Enable (comma-separated)</Label>
            <Input value={formData.pilotConfig.featureFlagsRequested} onChange={e => updateField('pilotConfig.featureFlagsRequested', e.target.value)} placeholder="e.g. attendance_tracking_enabled" data-testid="pilot-flags" />
          </div>
          <div>
            <Label>Allowed States</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {states.map(s => (
                <label key={s.code} className={`flex items-center gap-1.5 px-2 py-1 rounded border text-xs cursor-pointer ${(formData.pilotConfig.allowedStates || []).includes(s.code) ? 'bg-primary/10 border-primary' : 'hover:bg-muted'}`}>
                  <Checkbox checked={(formData.pilotConfig.allowedStates || []).includes(s.code)} onCheckedChange={() => toggleArrayItem('pilotConfig.allowedStates', s.code)} />
                  {s.code}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>Allowed Branches</Label>
            <div className="flex flex-wrap gap-2 mt-1">
              {branches.map(b => (
                <label key={b.code} className={`flex items-center gap-1.5 px-2 py-1 rounded border text-xs cursor-pointer ${(formData.pilotConfig.allowedBranches || []).includes(b.code) ? 'bg-primary/10 border-primary' : 'hover:bg-muted'}`}>
                  <Checkbox checked={(formData.pilotConfig.allowedBranches || []).includes(b.code)} onCheckedChange={() => toggleArrayItem('pilotConfig.allowedBranches', b.code)} />
                  {b.name || b.code}
                </label>
              ))}
            </div>
          </div>
          <div>
            <Label>Rollback Notes</Label>
            <Textarea value={formData.pilotConfig.rollbackNotes} onChange={e => updateField('pilotConfig.rollbackNotes', e.target.value)} placeholder="Rollback procedure notes..." rows={2} data-testid="pilot-rollback" />
          </div>
        </div>
      )}
    </div>
  );
}

function StepConfirmation({ formData, updateField }) {
  return (
    <div className="space-y-4" data-testid="step-confirmation">
      <p className="text-sm text-muted-foreground">Review your configuration and confirm to complete the onboarding setup.</p>
      <Card className="bg-muted/50">
        <CardContent className="pt-4 space-y-2 text-sm">
          <p><span className="font-medium">Name:</span> {formData.onboardingName || '-'}</p>
          <p><span className="font-medium">Type:</span> {TYPES.find(t => t.value === formData.type)?.label || formData.type}</p>
          <p><span className="font-medium">Start:</span> {formData.targetStartDate || 'Not set'}</p>
          <p><span className="font-medium">Go-Live:</span> {formData.targetGoLiveDate || 'Not set'}</p>
          <p><span className="font-medium">Modules:</span> {(formData.moduleConfig.modules || []).join(', ') || 'None'}</p>
          <p><span className="font-medium">Pilot:</span> {formData.pilotConfig.pilotEnabled ? `Enabled (${formData.pilotConfig.targetPilotStage || 'No stage'})` : 'Disabled'}</p>
        </CardContent>
      </Card>
      <Separator />
      <div className="space-y-3">
        <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted">
          <Checkbox checked={formData.confirmation.adminConfirm} onCheckedChange={v => updateField('confirmation.adminConfirm', v)} data-testid="confirm-admin" />
          <span className="text-sm">I confirm this onboarding project is configured correctly</span>
        </label>
        <label className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer hover:bg-muted">
          <Checkbox checked={formData.confirmation.reviewerConfirm} onCheckedChange={v => updateField('confirmation.reviewerConfirm', v)} data-testid="confirm-reviewer" />
          <span className="text-sm">Reviewer has approved this configuration</span>
        </label>
      </div>
      <div>
        <Label>Go-Live Notes</Label>
        <Textarea value={formData.confirmation.goLiveNotes} onChange={e => updateField('confirmation.goLiveNotes', e.target.value)} placeholder="Any final notes..." rows={2} data-testid="golive-notes" />
      </div>
    </div>
  );
}
